import { useCallback, useEffect, useMemo, useState } from 'react';

import { App, Button, Drawer, Empty, Space, Tag } from 'antd';
import { AlertTriangle, CheckCircle2, CloudRain, ExternalLink, MapPin, Radio } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetSensorTypesQuery } from '@/features/admin/api/opsManagementApi';
import { sensorTypeCatalogKeyLower } from '@/features/admin/utils/sensorTypeCatalogKey';
import { requestOpenIotStationDetail, requestStationFlyTo } from '@/features/map/stores/mapSlice';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import {
  acknowledgeOpenAlertsForIotStations,
  ackedIotStationIdsForBreaches,
  showIotAlertAckFeedback,
} from '@/features/realtime/utils/acknowledgeOpenIotAlerts';
import {
  formatStationValueSummary,
  maxRainfallFromStation,
  RAINFALL_ROUTING_KEY,
  stationHasRainfallChannel,
} from '@/features/realtime/utils/formatStationChannelReadings';
import {
  formatRelativeStationTime,
  isStationTelemetryStale,
} from '@/features/realtime/utils/iotStationOperationalSummary';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

export type KpiDetailKind = 'iot' | 'alerts' | 'rain';

export interface KPIDetailDrawerProps {
  open: boolean;
  kind: KpiDetailKind;
  onClose: () => void;
  stations: IoTDataPoint[];
  maxRain: { value: number; name: string; unit: string } | null;
  rainfallMaxIsZero: boolean;
}

function sName(s: IoTDataPoint): string {
  return (s.name ?? s.id).trim() || s.id;
}

function typeL(t: (k: string, o?: { defaultValue?: string }) => string, type?: string): string {
  if (!type) return '—';
  return t(`layerPanel.iotTypes.${type}`, { defaultValue: type });
}

const CARD: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid var(--glass-border)',
  background: 'var(--mdm-header-tint)',
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 8,
};

/**
 * Drawer chi tiết KPI — hiển thị thông tin hành động thực tế cho từng loại (iot / alerts / rain).
 */
export function KPIDetailDrawer({
  open,
  kind,
  onClose,
  stations,
  maxRain,
  rainfallMaxIsZero,
}: KPIDetailDrawerProps) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((s) => s.map.activeTab);
  const [ackLoadingId, setAckLoadingId] = useState<string | null>(null);
  const [ackUiRevision, setAckUiRevision] = useState(0);

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [open]);
  const flyZoom = activeTab === '3d' ? 15 : 14;

  const operational = shouldFetchOperationalApi();
  const { data: sensorTypes = [] } = useGetSensorTypesQuery(undefined, { skip: !operational });

  const sensorTypeByCodeLower = useMemo(() => {
    const m = new Map<string, string>();
    for (const st of sensorTypes) {
      const k = sensorTypeCatalogKeyLower(st);
      if (k && st.name) m.set(k, st.name.trim());
    }
    return m;
  }, [sensorTypes]);

  const getStationTypeName = useCallback(
    (s: IoTDataPoint) => {
      const code = s.sensorChannelCode?.trim().toLowerCase();
      if (code) {
        const fromMeta = sensorTypeByCodeLower.get(code);
        if (fromMeta) return fromMeta;
      }
      return typeL(t, s.type);
    },
    [sensorTypeByCodeLower, t],
  );

  /* ── Actions ─────────────────────────────────────────────────── */
  const flyTo = useCallback(
    (s: IoTDataPoint) => {
      const [longitude, latitude] = s.position;
      dispatch(requestStationFlyTo({ longitude, latitude, zoom: flyZoom }));
      onClose();
    },
    [dispatch, flyZoom, onClose],
  );

  const flyAndDetail = useCallback(
    (s: IoTDataPoint, initialRoutingKey?: string) => {
      const [longitude, latitude] = s.position;
      dispatch(requestStationFlyTo({ longitude, latitude, zoom: flyZoom }));
      dispatch(
        requestOpenIotStationDetail({
          stationId: s.id,
          ...(initialRoutingKey?.trim() ? { initialRoutingKey: initialRoutingKey.trim() } : {}),
        }),
      );
      onClose();
    },
    [dispatch, flyZoom, onClose],
  );

  /* ── Data ──────────────────────────────────────────────────────── */
  const onlineCount = useMemo(
    () => stations.filter((s) => s.status === 'online').length,
    [stations],
  );
  const offlineCount = stations.length - onlineCount;

  const needsAttention = useMemo(
    () =>
      stations
        .filter((s) => s.status === 'offline' || isStationTelemetryStale(s, nowMs))
        .sort((a, b) => {
          const aScore = a.status === 'offline' ? 0 : 1;
          const bScore = b.status === 'offline' ? 0 : 1;
          return aScore - bScore || sName(a).localeCompare(sName(b));
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stations],
  );

  const alertStations = useMemo(
    () =>
      stations
        .filter((s) => s.thresholdBreached)
        .sort((a, b) => {
          const rank = (x: IoTDataPoint) => (x.thresholdSeverity === 'danger' ? 0 : 1);
          return rank(a) - rank(b);
        }),
    [stations],
  );

  const ackedStationIds = useMemo(() => {
    void ackUiRevision;
    return ackedIotStationIdsForBreaches(alertStations.map((s) => s.id));
  }, [alertStations, ackUiRevision]);

  const alertPending = useMemo(
    () => alertStations.filter((s) => !ackedStationIds.has(s.id)),
    [alertStations, ackedStationIds],
  );

  const alertAcked = useMemo(
    () => alertStations.filter((s) => ackedStationIds.has(s.id)),
    [alertStations, ackedStationIds],
  );

  const handleAck = useCallback(
    async (s: IoTDataPoint) => {
      if (ackLoadingId !== null || ackedStationIds.has(s.id)) return;
      setAckLoadingId(s.id);
      try {
        const result = await acknowledgeOpenAlertsForIotStations([s]);
        showIotAlertAckFeedback(message, t, result, stations);
        if (result.acked.length > 0) setAckUiRevision((n) => n + 1);
      } catch {
        message.error(t('realtime.iotNetwork.headerDialogAckError'));
      } finally {
        setAckLoadingId(null);
      }
    },
    [ackLoadingId, ackedStationIds, message, stations, t],
  );

  const rainStations = useMemo(
    () =>
      stations
        .filter(stationHasRainfallChannel)
        .sort((a, b) => (maxRainfallFromStation(b) ?? 0) - (maxRainfallFromStation(a) ?? 0)),
    [stations],
  );

  /** Phân bố theo mức ngưỡng */
  const thresholdDist = useMemo(() => {
    const normal = stations.filter((s) => !s.thresholdBreached).length;
    const warning = stations.filter(
      (s) => s.thresholdBreached && s.thresholdSeverity !== 'danger',
    ).length;
    const danger = stations.filter(
      (s) => s.thresholdBreached && s.thresholdSeverity === 'danger',
    ).length;
    return { normal, warning, danger };
  }, [stations]);

  /** Phân bố theo loại cảm biến */
  const typeDist = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of stations) {
      const key = getStationTypeName(s);
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [stations, getStationTypeName]);

  /** Top 5 trạm cập nhật gần nhất có giá trị */
  const topRecent = useMemo(
    () =>
      [...stations]
        .filter((s) => Number.isFinite(s.value) && s.status === 'online')
        .sort((a, b) => (b.lastUpdated ?? 0) - (a.lastUpdated ?? 0))
        .slice(0, 5),
    [stations],
  );

  const titleMap: Record<KpiDetailKind, string> = {
    iot: t('kpi.detailDrawerIotTitle'),
    alerts: t('kpi.detailDrawerAlertsTitle'),
    rain: t('kpi.detailDrawerRainTitle'),
  };

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <Drawer
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {kind === 'iot' && <Radio size={14} color="#06b6d4" />}
          {kind === 'alerts' && <AlertTriangle size={14} color="#f97316" />}
          {kind === 'rain' && <CloudRain size={14} color="#6366f1" />}
          {titleMap[kind]}
        </span>
      }
      placement="right"
      width={Math.min(400, typeof window !== 'undefined' ? window.innerWidth - 16 : 400)}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{
        body: {
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          overflowY: 'auto',
        },
      }}
    >
      {/* ═══════════════════════ IoT kind ══════════════════════════ */}
      {kind === 'iot' && (
        <>
          {/* Summary pills */}
          <div style={{ display: 'flex', gap: 8 }}>
            {(
              [
                { label: 'Online', count: onlineCount, color: '#22c55e', dimWhenZero: false },
                { label: 'Offline', count: offlineCount, color: '#ef4444', dimWhenZero: true },
                {
                  label: 'Tổng',
                  count: stations.length,
                  color: 'var(--neon-cyan)',
                  dimWhenZero: false,
                },
              ] as const
            ).map((pill) => {
              const isZero = pill.count === 0;
              const showDim = pill.dimWhenZero && isZero;
              return (
                <div
                  key={pill.label}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '10px 8px',
                    borderRadius: 8,
                    border: `1px solid ${showDim ? 'var(--glass-border)' : `${pill.color}44`}`,
                    background: showDim ? 'transparent' : `${pill.color}10`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: showDim ? 'var(--text-muted)' : pill.color,
                    }}
                  >
                    {pill.count}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'var(--text-secondary)',
                      marginTop: 2,
                      fontWeight: 600,
                    }}
                  >
                    {pill.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Needs attention */}
          <div style={SECTION_LABEL}>
            {needsAttention.length > 0 ? `Cần chú ý (${needsAttention.length})` : 'Trạng thái trạm'}
          </div>
          {/* Threshold distribution + type breakdown when all OK */}
          {needsAttention.length === 0 ? (
            <>
              {/* Threshold distribution */}
              <div style={SECTION_LABEL}>Phân bố theo mức ngưỡng</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(
                  [
                    { label: 'Bình thường', count: thresholdDist.normal, color: '#22c55e' },
                    { label: 'Cảnh báo', count: thresholdDist.warning, color: '#eab308' },
                    { label: 'Nguy hiểm', count: thresholdDist.danger, color: '#ef4444' },
                  ] as const
                ).map((item) => (
                  <div
                    key={item.label}
                    style={{
                      flex: 1,
                      textAlign: 'center',
                      padding: '10px 6px',
                      borderRadius: 8,
                      border: `1px solid ${item.count > 0 ? `${item.color}44` : 'var(--glass-border)'}`,
                      background: item.count > 0 ? `${item.color}10` : 'transparent',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: item.count > 0 ? item.color : 'var(--text-muted)',
                      }}
                    >
                      {item.count}
                    </div>
                    <div
                      style={{
                        fontSize: 9,
                        color: 'var(--text-secondary)',
                        marginTop: 2,
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sensor type breakdown */}
              <div style={SECTION_LABEL}>Phân bố theo loại cảm biến</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {typeDist.map(([type, count]) => {
                  const pct = stations.length > 0 ? Math.round((count / stations.length) * 100) : 0;
                  return (
                    <div
                      key={type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '7px 10px',
                        borderRadius: 6,
                        background: 'var(--mdm-header-tint)',
                        border: '1px solid var(--glass-border)',
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          fontSize: 12,
                          color: 'var(--text-primary)',
                          fontWeight: 500,
                        }}
                      >
                        {type}
                      </span>
                      <div
                        style={{
                          flex: 2,
                          position: 'relative',
                          height: 4,
                          borderRadius: 2,
                          background: 'var(--glass-border)',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${pct}%`,
                            borderRadius: 2,
                            background: 'var(--neon-cyan)',
                          }}
                        />
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'var(--text-secondary)',
                          minWidth: 28,
                          textAlign: 'right',
                        }}
                      >
                        {count}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          color: 'var(--text-muted)',
                          minWidth: 30,
                          textAlign: 'right',
                        }}
                      >
                        {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Top recent stations */}
              {topRecent.length > 0 && (
                <>
                  <div style={SECTION_LABEL}>Cập nhật gần nhất</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {topRecent.map((s) => (
                      <div
                        key={s.id}
                        style={{
                          ...CARD,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <CheckCircle2 size={12} color="#22c55e" style={{ flexShrink: 0 }} />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                          }}
                        >
                          {sName(s)}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: 'var(--neon-cyan)',
                            flexShrink: 0,
                          }}
                        >
                          {s.value.toFixed(1)}
                          {s.unit ? ` ${s.unit}` : ''}
                        </span>
                        <Button
                          size="small"
                          type="text"
                          icon={<MapPin size={11} />}
                          onClick={() => flyTo(s)}
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div
              className="custom-scrollbar"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                overflowY: 'auto',
                maxHeight: '58vh',
              }}
            >
              {needsAttention.map((s) => {
                const isOffline = s.status === 'offline';
                const stale = isStationTelemetryStale(s, nowMs);
                const accentColor = isOffline ? '#ef4444' : '#eab308';
                return (
                  <div
                    key={s.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1px solid ${accentColor}44`,
                      background: `${accentColor}08`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span
                        style={{
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          flex: 1,
                          minWidth: 0,
                          fontSize: 13,
                        }}
                      >
                        {sName(s)}
                      </span>
                      <Tag color={isOffline ? 'red' : 'orange'} style={{ margin: 0, fontSize: 10 }}>
                        {isOffline ? 'Ngoại tuyến' : stale ? 'Dữ liệu cũ' : 'Chú ý'}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                      {getStationTypeName(s)} ·{' '}
                      {s.lastUpdated
                        ? formatRelativeStationTime(s.lastUpdated, i18n.language)
                        : '—'}
                    </div>
                    <Space size={6}>
                      <Button
                        size="small"
                        type="primary"
                        icon={<MapPin size={12} />}
                        onClick={() => flyTo(s)}
                      >
                        Vị trí
                      </Button>
                      <Button
                        size="small"
                        icon={<ExternalLink size={12} />}
                        onClick={() => flyAndDetail(s)}
                      >
                        Chi tiết
                      </Button>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ════════════════════ Alerts kind ═════════════════════════ */}
      {kind === 'alerts' && (
        <>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {t('kpi.detailDrawerIntroAlerts')}
          </p>
          {alertStations.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                padding: '40px 16px',
                borderRadius: 10,
                border: '1px solid rgba(34,197,94,0.25)',
                background: 'rgba(34,197,94,0.05)',
                color: '#22c55e',
                textAlign: 'center',
              }}
            >
              <CheckCircle2 size={32} />
              <div style={{ fontSize: 14, fontWeight: 700 }}>{t('kpi.detailDrawerNoAlerts')}</div>
            </div>
          ) : (
            <div
              className="custom-scrollbar"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                overflowY: 'auto',
                maxHeight: '75vh',
              }}
            >
              {alertPending.length > 0 ? (
                <div>
                  <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                    {t('realtime.iotNetwork.headerDialogAckSectionPending', {
                      count: alertPending.length,
                    })}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {alertPending.map((s) => {
                      const isDanger = s.thresholdSeverity === 'danger';
                      const accentColor = isDanger ? '#ef4444' : '#eab308';
                      return (
                        <div
                          key={s.id}
                          style={{
                            padding: '12px 14px',
                            borderRadius: 10,
                            border: `1px solid ${accentColor}44`,
                            background: `${accentColor}08`,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <AlertTriangle
                              size={14}
                              color={accentColor}
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            <span
                              style={{
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                flex: 1,
                                minWidth: 0,
                                lineHeight: 1.35,
                              }}
                            >
                              {sName(s)}
                            </span>
                            <Tag
                              color={isDanger ? 'red' : 'gold'}
                              style={{ margin: 0, fontWeight: 700 }}
                            >
                              {isDanger
                                ? t('realtime.iotNetwork.danger')
                                : t('realtime.iotNetwork.warning')}
                            </Tag>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--text-secondary)',
                              lineHeight: 1.45,
                              marginBottom: 8,
                            }}
                          >
                            <div>
                              {t('realtime.iotNetwork.headerDialogValueLabel')}:{' '}
                              <strong style={{ color: accentColor }}>
                                {formatStationValueSummary(s)}
                              </strong>
                            </div>
                            <div>
                              {t('realtime.iotNetwork.headerDialogSensorTypeLabel')}:{' '}
                              {getStationTypeName(s)}
                            </div>
                            {s.lastUpdated ? (
                              <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                                {formatRelativeStationTime(s.lastUpdated, i18n.language)}
                              </div>
                            ) : null}
                          </div>
                          <Space size={6} wrap>
                            <Button
                              size="small"
                              type="primary"
                              icon={<MapPin size={12} />}
                              onClick={() => flyTo(s)}
                            >
                              {t('realtime.iotNetwork.headerDialogFlyMap')}
                            </Button>
                            <Button
                              size="small"
                              icon={<ExternalLink size={12} />}
                              onClick={() => flyAndDetail(s)}
                            >
                              {t('realtime.iotNetwork.headerDialogOpenDetail')}
                            </Button>
                            <Button
                              size="small"
                              danger={isDanger}
                              loading={ackLoadingId === s.id}
                              onClick={() => handleAck(s)}
                            >
                              {t('realtime.iotNetwork.headerDialogAck')}
                            </Button>
                          </Space>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {alertAcked.length > 0 ? (
                <div>
                  <div style={{ ...SECTION_LABEL, marginBottom: 8 }}>
                    {t('realtime.iotNetwork.headerDialogAckSectionDone', {
                      count: alertAcked.length,
                    })}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {alertAcked.map((s) => {
                      const isDanger = s.thresholdSeverity === 'danger';
                      const accentColor = isDanger ? '#ef4444' : '#eab308';
                      return (
                        <div
                          key={s.id}
                          style={{
                            padding: '12px 14px',
                            borderRadius: 10,
                            border: '1px solid rgba(34,197,94,0.35)',
                            background: 'rgba(34,197,94,0.06)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              marginBottom: 8,
                            }}
                          >
                            <CheckCircle2
                              size={14}
                              color="#22c55e"
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            <span
                              style={{
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                flex: 1,
                                minWidth: 0,
                                lineHeight: 1.35,
                              }}
                            >
                              {sName(s)}
                            </span>
                            <Tag color="success" style={{ margin: 0 }}>
                              {t('realtime.iotNetwork.headerDialogAckBadge')}
                            </Tag>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--text-secondary)',
                              lineHeight: 1.45,
                              marginBottom: 8,
                            }}
                          >
                            <div>
                              {t('realtime.iotNetwork.headerDialogValueLabel')}:{' '}
                              <strong style={{ color: accentColor }}>
                                {formatStationValueSummary(s)}
                              </strong>
                              <span style={{ marginLeft: 6, fontWeight: 500 }}>
                                (
                                {isDanger
                                  ? t('realtime.iotNetwork.danger')
                                  : t('realtime.iotNetwork.warning')}
                                )
                              </span>
                            </div>
                            <div>
                              {t('realtime.iotNetwork.headerDialogSensorTypeLabel')}:{' '}
                              {getStationTypeName(s)}
                            </div>
                            {s.lastUpdated ? (
                              <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                                {formatRelativeStationTime(s.lastUpdated, i18n.language)}
                              </div>
                            ) : null}
                          </div>
                          <Space size={6} wrap>
                            <Button
                              size="small"
                              type="primary"
                              icon={<MapPin size={12} />}
                              onClick={() => flyTo(s)}
                            >
                              {t('realtime.iotNetwork.headerDialogFlyMap')}
                            </Button>
                            <Button
                              size="small"
                              icon={<ExternalLink size={12} />}
                              onClick={() => flyAndDetail(s)}
                            >
                              {t('realtime.iotNetwork.headerDialogOpenDetail')}
                            </Button>
                          </Space>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════ Rain kind ═════════════════════════ */}
      {kind === 'rain' && (
        <>
          {rainStations.length === 0 ? (
            <Empty description={t('kpi.rainfallNoStations')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {/* Hero: Stable Status or Max Station */}
              {rainfallMaxIsZero ? (
                <div
                  style={{
                    padding: '20px 16px',
                    borderRadius: 12,
                    background: 'rgba(34,197,94,0.1)',
                    border: '1px solid rgba(34,197,94,0.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    marginBottom: 20,
                  }}
                >
                  <CheckCircle2 size={32} color="#22c55e" style={{ marginBottom: 10 }} />
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Hệ thống ổn định
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    Không ghi nhận lượng mưa tại {rainStations.length} trạm quan trắc
                  </div>
                </div>
              ) : (
                maxRain && (
                  <div
                    style={{
                      padding: '16px',
                      borderRadius: 12,
                      background:
                        'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(99,102,241,0.05) 100%)',
                      border: '1px solid rgba(99,102,241,0.3)',
                      marginBottom: 20,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        right: -10,
                        top: -10,
                        opacity: 0.1,
                        transform: 'rotate(-15deg)',
                      }}
                    >
                      <CloudRain size={80} color="#6366f1" />
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#6366f1',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: 12,
                      }}
                    >
                      Lượng mưa cao nhất
                    </div>
                    <div
                      style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}
                    >
                      <span
                        style={{ fontSize: 36, fontWeight: 800, color: '#6366f1', lineHeight: 1 }}
                      >
                        {maxRain.value.toFixed(1)}
                      </span>
                      <span
                        style={{ fontSize: 16, color: 'var(--text-secondary)', fontWeight: 600 }}
                      >
                        {maxRain.unit}
                      </span>
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        marginBottom: 16,
                        fontSize: 15,
                        maxWidth: '80%',
                      }}
                    >
                      {maxRain.name}
                    </div>
                    {(() => {
                      const hero = rainStations.find((s) => sName(s) === maxRain.name);
                      return hero ? (
                        <Space size={8}>
                          <Button
                            type="primary"
                            icon={<MapPin size={14} />}
                            onClick={() => flyTo(hero)}
                            style={{ borderRadius: 6 }}
                          >
                            Vị trí
                          </Button>
                          <Button
                            icon={<ExternalLink size={14} />}
                            onClick={() => flyAndDetail(hero, RAINFALL_ROUTING_KEY)}
                            style={{ borderRadius: 6 }}
                          >
                            Chi tiết
                          </Button>
                        </Space>
                      ) : null;
                    })()}
                  </div>
                )
              )}

              {/* Grouped Stations by Basin */}
              <div
                className="custom-scrollbar"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 350px)',
                  paddingRight: 4,
                }}
              >
                {(() => {
                  // Group by basin
                  const groups: Record<string, IoTDataPoint[]> = {};
                  rainStations.forEach((s) => {
                    const b = s.basinName?.trim() || 'Khác';
                    if (!groups[b]) groups[b] = [];
                    groups[b].push(s);
                  });

                  return Object.entries(groups)
                    .sort(([a], [b]) => (a === 'Khác' ? 1 : b === 'Khác' ? -1 : a.localeCompare(b)))
                    .map(([basin, stns]) => (
                      <div key={basin}>
                        <div
                          style={{
                            ...SECTION_LABEL,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid var(--glass-border)',
                            paddingBottom: 6,
                            marginBottom: 10,
                          }}
                        >
                          <span>{basin}</span>
                          <Tag style={{ margin: 0, fontSize: 9, opacity: 0.8 }}>
                            {stns.length} trạm
                          </Tag>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {stns
                            .sort(
                              (a, b) =>
                                (maxRainfallFromStation(b) ?? 0) - (maxRainfallFromStation(a) ?? 0),
                            )
                            .map((s) => {
                              const rainMm = maxRainfallFromStation(s);
                              const hasRain = rainMm != null && rainMm > 0;
                              return (
                                <div
                                  key={s.id}
                                  style={{
                                    ...CARD,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    background: hasRain ? 'rgba(99,102,241,0.05)' : CARD.background,
                                    border: hasRain
                                      ? '1px solid rgba(99,102,241,0.2)'
                                      : CARD.border,
                                  }}
                                >
                                  <CloudRain
                                    size={14}
                                    color={hasRain ? '#6366f1' : 'var(--text-muted)'}
                                    style={{ flexShrink: 0 }}
                                  />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: 'var(--text-primary)',
                                        fontSize: 13,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {sName(s)}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 700,
                                      color: hasRain ? '#6366f1' : 'var(--text-muted)',
                                      flexShrink: 0,
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {rainMm != null && Number.isFinite(rainMm)
                                      ? rainMm.toFixed(1)
                                      : '—'}
                                    <span style={{ fontSize: 10, marginLeft: 2, fontWeight: 400 }}>
                                      mm
                                    </span>
                                  </div>
                                  <Space size={4} style={{ flexShrink: 0 }}>
                                    <Button
                                      size="small"
                                      type="text"
                                      icon={<MapPin size={14} />}
                                      onClick={() => flyTo(s)}
                                      title="Vị trí"
                                    />
                                    <Button
                                      size="small"
                                      type="text"
                                      icon={<ExternalLink size={14} />}
                                      onClick={() => flyAndDetail(s, RAINFALL_ROUTING_KEY)}
                                      title="Chi tiết"
                                    />
                                  </Space>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ));
                })()}
              </div>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}
